import { createTheme } from "@mui/material"
import defaultTheme from "./default"
import { ExpandMore } from '@mui/icons-material'

function getFullscreenElement () {
    if ( typeof document === "undefined" ) return null
    // Standard + vendor-prefixed fallbacks
    return (
        document.fullscreenElement ??
        ( document ).webkitFullscreenElement ??
        ( document ).mozFullScreenElement ??
        ( document ).msFullscreenElement ??
        null
    )
}

const lightTheme = createTheme( {
    theme: 'light',
    border: [
        "none",
        '1px solid #E4E4E7',
    ],
    shadows: [
        "none",
        "0px 0px 4px 1px rgba(0,0,0,0.1)",
        ...defaultTheme.shadows.slice( 2 )
    ],
    table: {
        headerBg: "#09090b",
        headerColor: '#000000',
        color: '#09090b',
        fontSize: "14px"
    },
    zIndex: {
        modal: 3000,
        tooltip: 5000,
    },
    layout: {
        pagePadding: { md: "20px", xs: "10px" },
        borderRadius: "5px",
        cardsGap: "10px",
        sectionsGap: "20px",
    },
    palette: {
        common: { white: "#fff", black: "#000", font: "#09090b", bg: "#fff" },
        mode: 'light',
        borderColor: "#D7D7D7",
        contentBg: "#f0f0f0",
        defaultColor: "#000",
        shadedText: "#373737",
        greyedOut: "#E5E4E2",
        layoutBackground: "#05005c" || `linear-gradient( 90deg, rgb(3, 0, 52) 25.54%, rgb(10, 0, 154) 205.66% )`,
        cardBackground: "#fff",
        contrastText: "#000",
        background: { paper: "#fff", default: "#fff" },

        form: {
            labelColor: '#09090b',
            formCardBg: "#ffffff",
            formBg: "#FCFCFC",
            placeholder: "#b3b3b3",
            inputBg: "#fff"
        },
        nav: {
            font: "#ffffff",
            sidebar: "linear-gradient(90deg, #030034 25.54%, #0A009A 205.66%)",
            sidebarHover: "#FFFFFF1F",
            activeSidebarItem: "#db8d06",
            selected: "#151D48"
        },
        primary: {
            main: '#05005c',
            light: '#29248b',
            dark: '#03013b',
            contrastText: '#f8fafc'
        },
        primaryDark: {
            main: '#05004E',
            light: '#3c4994',
            dark: '#101636',
            contrastText: '#fff'
        },
        errorMessage: {
            main: "#F44336",
            light: "#FF8D9D",
            dark: "#de5f71",
            contrastText: "#fff"
        },
        customThemeColor: {
            main: "#E25A26",
            light: "#d17959",
            dark: "#d94c16",
            contrastText: "#fff"
        },
        greyed: {
            main: "#18181b",
            light: "#27272a",
            dark: "#09090b",
            contrastText: "#ffffff"
        },
        text: {
            secondary: "#71717a",
            primary: "#09090b",
        },
        rowHover: "#f7f7f7"
    },
    typography: {
        fontFamily: "'Roboto', sans-serif",
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h1',
                    h3: 'h1',
                    h4: 'h1',
                    h5: 'h1',
                    h6: 'h1',
                    subtitle1: 'h6',
                    subtitle2: 'span',
                    body1: 'p',
                    body2: 'p',
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: "10px"
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: "5px",
                    border: "1px solid #E4E4E7",
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    border: "none",
                    fontSize: "14px"
                }
            }
        },
        MuiInputBase: {
            defaultProps: {
                color: "secondary",
            },
            styleOverrides: {
                root: {
                    background: "#fff",
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2", // optional
                        borderWidth: "0px", // keep original border
                    },
                    "&.Mui-focused": {
                        boxShadow: "0 0 0 4px rgba(116, 116, 116, 0.4)", // glow effect outside
                    },
                },
                input: {
                    fontSize: "14px",
                    fontWeight: "300"
                }
            },
        },
        MuiMenu: {
            defaultProps: {
                autoFocus: true
            },
            styleOverrides: {
                paper: {
                    padding: "0",
                    borderRadius: "5px"
                }
            }
        },
        MuiDialog: {
            defaultProps: {
                slotProps: {
                    paper: {
                        className: "custom-scrollbar",
                        sx: { width: { sm: "90%", xs: "100%" }, maxWidth: "1024px" }
                    }
                }
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0,
                }
            }
        },
        MuiTooltip: {
            defaultProps: {
                slotProps: {
                    popper: {
                        container: ( anchorEl ) => {
                            const fs = getFullscreenElement()
                            // Prefer an HTMLElement; otherwise fall back to body
                            return ( fs instanceof HTMLElement ? fs : document.body )
                        }
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    "&.Mui-focused": {
                        boxShadow: "none", // glow effect outside
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: defaultTheme.palette.divider, // Much darker on focus
                        borderWidth: '1px',   // Optional: make it slightly thicker
                    },
                }
            },
            defaultProps: {
                IconComponent: ExpandMore,
                MenuProps: {
                    anchorOrigin: {
                        vertical: "bottom", horizontal: "left"
                    },
                    transformOrigin: {
                        vertical: "top", horizontal: "left"
                    },
                    slotProps: {
                        paper: {
                            sx: { marginTop: "5px", border: `1px solid #E4E4E7` }
                        }
                    }
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    margin: 0,
                    marginTop: "3px",
                    color: "#F44336",
                }
            }
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "capitalize",
                    // background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    margin: "5px",
                    borderRadius: "5px",
                    padding: "5px 10px",
                    ":hover": {
                        background: "#fef3e766",
                    },
                    '&.Mui-selected:hover': {
                        background: "#fef3e7",
                        color: "#d4770c"
                    },
                    '&.Mui-selected': {
                        background: "#fef3e7",
                        color: "#d4770c"
                    },
                }
            }
        },

        MuiSkeleton: {
            defaultProps: {
                animation: "wave"
            },
            styleOverrides: {
                root: {
                    transform: "scale(1)",
                    borderRadius: 10,
                }
            }
        },

        // CUSTOM COMPOENENTS FRO DATE AND TIME PICKERS

        MuiPickerOutlinedInput: {
            defaultProps: {
                slotProps: {
                    textField: {
                        size: "small",
                        slotProps: {
                            input: {
                                sx: {
                                    bgcolor: "#fff"
                                }
                            }
                        }
                    },
                    desktopPaper: {
                        sx: {
                            border: '1px solid #E4E4E7',
                            marginY: "5px",
                            bgcolor: "#fff",
                            boxShadow: `0px 1px 4px 1px rgba(0,0,0,0.1)`,
                        }
                    },
                    mobilePaper: {
                        sx: {
                            border: '1px solid #E4E4E7',
                            marginY: "5px",
                            bgcolor: "#fff",
                            boxShadow: `0px 1px 4px 1px rgba(0,0,0,0.1)`,
                        }
                    },
                    popper: {
                        sx: {
                            margin: "10px",
                            zIndex: defaultTheme.zIndex.tooltip * 4
                        }
                    }
                }
            }
        },
    }
} )

export default lightTheme